import React from 'react'
import { Link } from 'react-head'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import DatoImage from '../../components/DatoRichText/DatoImage'
import convert from 'htmr'
import NutritionArticleBlock from './NutritionArticleBlock'
import { runQuery, getDatoLocale, datoLocalesToLanguages } from 'js/dato'
import { query } from 'js/pages/NutritionExplained/queries/nutritionArticleQuery'
import Footer from 'js/components/Footer/Footer'
import { Navigation } from 'js/pages/Home/Navigation'
import RelatedStories from './components/RelatedStories'
import calculateReadingTime from './utils/calculateReadingTime'
import DecorativeCircles from './components/DecorativeCircles'
import ArticleMeta from './components/ArticleMeta'
import AppPromo from './components/AppPromo'
import DatoMeta from '../../components/DatoMeta'
import ShareButtons from '../../components/ShareButtons'
import Fold from 'js/components/Fold'
import { NutritionArticle_nutritionArticle } from './queries/types/NutritionArticle'
import References from 'js/components/References/References'
import NotMedicalAdviceDisclaimer from 'js/components/NotMedicalAdviceDisclaimer'

type Props = {
  data: NutritionArticle_nutritionArticle
  isLoading: boolean
  error: any
}

function NutritionArticle({ data, isLoading, error }: Props) {
  if (error) {
    return <ErrorPage error={error} />
  }

  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  const {
    publishDate,
    image,
    title,
    excerpt,
    introText,
    categories,
    content,
    references,
    id,
    meta,
    slug,
  } = data

  const readingTime = calculateReadingTime(data)
  const availableLanguages = datoLocalesToLanguages(data?._locales ?? [])

  return (
    <>
      <DatoMeta
        meta={meta}
        fallbackDescription={excerpt ? excerpt : undefined}
        canonicalUrl={`https://lifesum.com/nutrition-explained/${slug}`}
      />
      {image?.responsiveImage && (
        <Link
          rel="preload"
          as="image"
          href={image.responsiveImage.src}
          imagesrcset={image.responsiveImage.webpSrcSet}
          imagesizes="90vw"
        />
      )}
      <div className="bg-BgSub py-60">
        <Navigation
          theme="sub"
          minimal={false}
          availableLanguages={availableLanguages}
        />
      </div>
      <div className="bg-Bg pb-128">
        <div className="bg-BgSub pb-64 pt-30 md:pt-48 lg:pb-128">
          <div
            className={`
              o-width-limiter o-width-limiter--narrow
              lg:py-24 xxl:py-32
            `}
          >
            <div className="items-center text-center lg:flex lg:text-left">
              <div className="relative z-10 lg:w-2/3">
                <h1 className="type-large-fat text-green">{title}</h1>
                <p
                  className={`
                    mt-12
                    leading-body type-17 md:mt-16
                    md:type-24 lg:type-28
                  `}
                >
                  {excerpt}
                </p>
              </div>
              <div className="relative flex lg:w-1/3">
                <DecorativeCircles big={categories.length > 1} />
                <div className="hidden w-full lg:flex">
                  <ArticleMeta
                    publishDate={publishDate}
                    categories={categories}
                    readingTime={readingTime}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`
          o-width-limiter o-width-limiter--narrow
          relative z-10
        `}
        >
          <figure className="-mt-48 lg:-mt-80 lg:mb-48">
            <DatoImage
              src={image?.responsiveImage?.src as string}
              width="1200"
              height="600"
              {...image?.responsiveImage}
              title={image?.responsiveImage?.title || undefined}
              className="w-full rounded-8 md:rounded-12 lg:rounded-20"
            />
            {image?.responsiveImage?.title && (
              <figcaption className="mt-12 leading-body text-TypeSub">
                {image.responsiveImage.title}
              </figcaption>
            )}
          </figure>
          <div className="mb-24 md:mb-32 lg:mb-48 lg:hidden">
            <ArticleMeta
              publishDate={publishDate}
              categories={categories}
              readingTime={readingTime}
            />
          </div>
          <div className="mx-auto max-w-768">
            <div className="s-text">
              {introText && (
                <div className="type-med-minus">{convert(introText)}</div>
              )}
            </div>
            {content.map(({ __typename: type, ...props }, index) => {
              return (
                <NutritionArticleBlock key={index} type={type} {...props} />
              )
            })}
            <Fold />
            <References references={references} />
            <NotMedicalAdviceDisclaimer />
            <div className="mt-48">
              <ShareButtons title={title} />
            </div>
          </div>
        </div>
        <div className="o-width-limiter ">
          {categories.length ? (
            <div className="mt-64">
              <RelatedStories category={categories[0]} not={id} />
            </div>
          ) : null}
        </div>
        <AppPromo />
      </div>
      <Footer marginless />
    </>
  )
}

NutritionArticle.getInitialProps = async ({ params, isPreview, language }) => {
  const locale = getDatoLocale(language || 'en')
  let { nutritionArticle } = await runQuery(
    query,
    { slug: params.slug, locale },
    isPreview,
  )

  // The record is matched by slug across all locales, so Dato still returns it
  // (with empty localized fields) even when it has no content in the requested
  // locale. A plain null check isn't enough — verify the requested locale is
  // actually one of the record's `_locales`, and otherwise fall back to English.
  const hasRequestedLocale =
    nutritionArticle && (nutritionArticle._locales ?? []).includes(locale)

  if (!hasRequestedLocale && locale !== 'en') {
    const { nutritionArticle: fallback } = await runQuery(
      query,
      { slug: params.slug, locale: 'en' },
      isPreview,
    )
    nutritionArticle = fallback ?? nutritionArticle ?? null
  }

  if (!nutritionArticle) return null

  return nutritionArticle
}

export default withSSR(NutritionArticle)
